'use client';

import type * as React from 'react';
import { useMemo } from 'react';

import { Avatar as AvatarPrimitive } from 'radix-ui';
import { twMerge } from 'tailwind-merge';

import { twSort } from '@/_shared';

const avatar = {
  base: twSort`text-c-card-muted bg-c-avatar! border-c-border-muted relative flex shrink-0 grow-0 overflow-hidden rounded-full border`,
  size: {
    lg: twSort`size-10`,
    default: twSort`size-8`,
    sm: twSort`size-6`,
  },
};

function Avatar({
  className,
  size = 'default',
  legacyBgColor = false,
  transparent = false,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: 'default' | 'sm' | 'lg';
  legacyBgColor?: boolean;
  transparent?: boolean;
}) {
  const cls = twMerge(
    avatar.base,
    avatar.size[size],
    className,
    legacyBgColor && 'bg-c-background!',
    transparent && 'bg-transparent! dark:bg-transparent!'
  );

  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={twMerge(
        // usage of data to implement dynamic size
        // data-[size=lg]:size-10 data-[size=sm]:size-6
        'group/avatar relative flex size-8 shrink-0 overflow-hidden rounded-full select-none',
        cls,
        className
      )}
      {...props}
    />
  );
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={twMerge('aspect-square size-full object-cover', className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  name,
  useFullName = false,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> & { name: string; useFullName?: boolean }) {
  const init = useMemo(() => getInit(name), [name]);
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={twMerge(
        'bg-muted text-muted-foreground flex size-full items-center justify-center rounded-full text-sm group-data-[size=sm]/avatar:text-xs',
        className
      )}
      {...props}
    >
      {useFullName ? name : init}
    </AvatarPrimitive.Fallback>
  );
}

function AvatarBadge({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar-badge"
      className={twMerge(
        'bg-primary text-primary-foreground ring-background absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full ring-2 select-none',
        'group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden',
        'group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2',
        'group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2',
        className
      )}
      {...props}
    />
  );
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={twMerge(
        '*:data-[slot=avatar]:ring-background group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2',
        className
      )}
      {...props}
    />
  );
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group-count"
      className={twMerge(
        'bg-muted text-muted-foreground ring-background relative flex size-8 shrink-0 items-center justify-center rounded-full text-sm ring-2 group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3',
        className
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarBadge, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage };

function getInit(name = '') {
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();

  if (names.length > 1) {
    initials += names[names.length - 1].substring(0, 1).toUpperCase();
  }

  return initials;
}
